import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { presupuestosApi } from '../api/presupuestos'
import EstadoBadgePresupuesto, { LABELS_PRESUPUESTO } from '../components/EstadoBadgePresupuesto'
import Boton from '../components/Boton'
import BotonMenu from '../components/BotonMenu'
import Modal from '../components/Modal'
import ModalListaClientes from '../components/ModalListaClientes'
import ModalAltaCliente from '../components/ModalAltaCliente'
import { copiarTexto } from '../utils/copiar'
import { fmtFecha, fmtFechaHora, fmtMonto } from '../utils/fecha'
import { colors, font, text, sx } from '../design/tokens'
import ImagenesPresupuesto from '../components/ImagenesPresupuesto'
import RecorridoPresupuesto, { ChipVersion } from '../components/RecorridoPresupuesto'

// Estados donde tiene sentido cambiar el cliente (antes de convertir a NV)
const ESTADOS_CAMBIO_CLIENTE = ['borrador', 'enviado', 'vencido', 'aceptado', 'aprobado']
// Estados finales de una versión: no admiten versión nueva
const ESTADOS_CERRADOS = ['convertido', 'reemplazado', 'perdido']

const cardSx = { ...sx.card, marginBottom: 14 }
const lblSx = { ...sx.cardTitle }
const inputSx = { padding: '7px 10px', border: `1px solid ${colors.textPlh}`, borderRadius: 6, fontSize: text.body }
// Bloque de cada vía de envío dentro del modal "Enviar al cliente"
const viaSx = { border: `1px solid ${colors.borderSoft}`, borderRadius: 8, padding: 12, marginBottom: 10 }

export default function PresupuestoDetallePage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [p, setP] = useState(null)
  const [modal, setModal] = useState(null) // 'aceptar' | 'rechazar' | 'devolver' | 'enviar' | 'cambiarCliente' | 'buscarCliente' | 'altaCliente'
  const [elegidas, setElegidas] = useState({}) // conjunto → grupo_id
  const [motivo, setMotivo] = useState('')
  const [cierre, setCierre] = useState('nueva_version') // qué pasa después del rechazo
  const [mail, setMail] = useState({ email: '', mensaje: '' })
  const [trabajando, setTrabajando] = useState(false)
  const [error, setError] = useState(null)

  const cargar = useCallback(async () => {
    const { data } = await presupuestosApi.obtener(id)
    setP(data)
    setMail(m => ({ ...m, email: data.cliente?.email || data.destinatario?.email || '' }))
  }, [id])

  useEffect(() => { cargar() }, [cargar])

  if (!p) return <div style={{ color: colors.textMeta }}>Cargando…</div>

  const t = p.totales
  const accion = async (fn, cerrar = true) => {
    setError(null)
    setTrabajando(true)
    try {
      await fn()
      if (cerrar) setModal(null)
      await cargar()
    } catch (e) {
      setError(e.response?.data?.message || Object.values(e.response?.data?.errors || {}).flat().join(' · ') || 'Error')
    } finally {
      setTrabajando(false)
    }
  }

  const linkPdfPublico = `${window.location.origin}/api/publico/presupuestos/${p.token_publico}/pdf`
  const compartirWA = () => {
    // Genérico: abre WhatsApp con el mensaje armado y el usuario elige el contacto.
    // window.open SINCRÓNICO (popup-blocker); el registro del canal va en paralelo.
    const texto = `Te paso el presupuesto ${p.numero} de Vinoteca Ligier: ${linkPdfPublico}`
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank')
    setModal(null)
    presupuestosApi.compartirWa(p.id).then(cargar).catch(() => {})
  }

  const conjuntos = Object.entries(t.conjuntos || {})

  const asignarCliente = (cli) => {
    accion(() => presupuestosApi.cambiarCliente(p.id, cli.id))
    setModal(null)
  }

  // Retomar = partir de una versión que no es la vigente (reemplazada, o la
  // venta ya cerrada): la nueva sale con el contenido de ESTA.
  const ultimaVersion = Math.max(p.version, ...(p.versiones || []).map(v => v.version))
  const retomar = p.version < ultimaVersion || ESTADOS_CERRADOS.includes(p.estado)

  return (
    <div style={{ maxWidth: 1000 }}>
      {/* Header: un número para toda la venta; las versiones son chips */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <Boton variante="ghost" sm onClick={() => navigate(-1)}>← Volver</Boton>
        <h1 style={{ margin: 0, fontFamily: font.mono, fontSize: text.display, userSelect: 'all' }}>{p.numero}</h1>
        <Boton variante="ghost" sm title="Copiar número" onClick={() => copiarTexto(p.numero)}>📋</Boton>
        <span style={{ display: 'inline-flex', gap: 4 }}>
          {(p.versiones || [{ id: p.id, version: p.version, estado: p.estado }]).map(v => (
            <ChipVersion key={v.id} version={v.version} activa={v.id === p.id}
              title={v.id === p.id ? 'Esta versión' : `Ver la versión ${v.version} (${LABELS_PRESUPUESTO[v.estado] || v.estado})`}
              onClick={v.id === p.id ? undefined : () => navigate(`/presupuestos/${v.id}`)} />
          ))}
        </span>
        <EstadoBadgePresupuesto estado={p.estado} />
        <span style={{ fontSize: text.sec, color: colors.textSec }}>{p.cliente?.nombre || p.destinatario?.nombre || '—'}{p.vendedor?.nombre ? ` · ${p.vendedor.nombre}` : ''}</span>
      </div>

      {/* Recorrido: dónde está la venta y qué toca hacer (la regla vive en el backend) */}
      <div style={{ ...cardSx, paddingBottom: 10 }}>
        <RecorridoPresupuesto recorrido={p.recorrido} />
      </div>

      {/* Toolbar: UNA acción primaria (el próximo paso del circuito) + el resto agrupado */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {p.estado === 'borrador' && <>
          <Boton variante="primary" onClick={() => setModal('enviar')}>✉ Enviar al cliente</Boton>
          <Boton variante="secondary" onClick={() => navigate(`/presupuestos/${p.id}/editar`)}>✎ Editar</Boton>
        </>}
        {['enviado', 'vencido'].includes(p.estado) && <>
          <Boton variante="primary" onClick={() => { setElegidas({}); setModal('aceptar') }}>✓ Aceptado por el cliente</Boton>
          <Boton variante="destructive" onClick={() => setModal('rechazar')}>✕ Rechazado</Boton>
          <Boton variante="send" onClick={() => setModal('enviar')}>✉ Reenviar / compartir</Boton>
        </>}
        {['aceptado', 'aprobado'].includes(p.estado) && <>
          <Boton variante="primary" onClick={() => accion(() => presupuestosApi.convertir(p.id))}>📄 Convertir a Nota de Venta</Boton>
          <Boton variante="secondary" onClick={() => { setMotivo(''); setModal('devolver') }}>↩ Devolver al vendedor</Boton>
        </>}
        {p.estado === 'rechazado' && <>
          <Boton variante="primary" onClick={() => accion(async () => {
            const { data } = await presupuestosApi.nuevaVersion(p.id)
            navigate(`/presupuestos/${data.id}/editar`)
          }, false)}>⧉ Nueva versión</Boton>
          <Boton variante="destructive" onClick={() => window.confirm('¿Dar por perdida esta venta? Cierra el recorrido.') && accion(() => presupuestosApi.perder(p.id))}>✕ Dar por perdido</Boton>
        </>}
        <BotonMenu label="⬇ Descargar" items={[
          { label: '⬇ PDF', onClick: () => presupuestosApi.descargar(p.id, 'pdf', p.numero) },
          { label: '⬇ Excel/CSV', onClick: () => presupuestosApi.descargar(p.id, 'csv', p.numero) },
          { label: '⬇ TXT', onClick: () => presupuestosApi.descargar(p.id, 'txt', p.numero) },
        ]} />
        <BotonMenu label="⋯ Más" items={[
          // Cualquier versión se puede retomar, aunque la venta esté convertida
          // o perdida (sug #91): nace la siguiente en borrador con ESTE contenido.
          p.estado !== 'rechazado' && {
            label: retomar ? `⧉ Retomar la v${p.version}` : '⧉ Nueva versión',
            onClick: () => (!retomar || window.confirm(`Se crea la v${ultimaVersion + 1} en borrador con el contenido de la v${p.version}.`
              + (p.estado === 'convertido' ? '' : ' La versión en curso pasa a reemplazada.') + ' ¿Seguimos?'))
              && accion(async () => {
                const { data } = await presupuestosApi.nuevaVersion(p.id)
                navigate(`/presupuestos/${data.id}/editar`)
              }, false),
          },
          p.estado === 'borrador' && {
            label: '🗑 Eliminar borrador', destructive: true,
            onClick: () => window.confirm('¿Eliminar este borrador?') && accion(async () => {
              await presupuestosApi.eliminar(p.id)
              navigate('/presupuestos', { replace: true })
            }, false),
          },
        ]} />
      </div>

      {error && <div style={{ color: colors.err, marginBottom: 10, fontSize: text.sec }}>{error}</div>}
      {p.estado === 'convertido' && p.nota_venta && (
        <div style={{ ...cardSx, background: colors.okBg, borderColor: colors.border }}>
          ✓ Convertido en la NV <Link to={`/notas-venta/${p.nota_venta.id}`} style={{ fontFamily: font.mono, fontWeight: 600 }}>{p.nota_venta.numero_lgr}</Link>
        </div>
      )}
      {['rechazado', 'perdido'].includes(p.estado) && p.motivo_rechazo && (
        <div style={{ ...cardSx, background: colors.errBg, borderColor: colors.border }}>✕ {p.estado === 'perdido' ? 'Perdido' : 'Rechazado'}: {p.motivo_rechazo}</div>
      )}

      {/* Datos */}
      <div style={{ ...cardSx, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <div>
          <div style={lblSx}>Cliente
            {!p.cliente_id && <span style={{ marginLeft: 6, fontSize: text.meta, color: colors.warn, fontWeight: 600 }}>· libre, sin alta</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>{p.cliente?.nombre || p.destinatario?.nombre || '—'}</span>
            {ESTADOS_CAMBIO_CLIENTE.includes(p.estado) && (
              <Boton variante="ghost" sm title="Cambiar cliente (buscar o dar de alta)"
                onClick={() => setModal('cambiarCliente')}>👤 Cambiar</Boton>
            )}
          </div>
          {(p.cliente?.cuit || p.destinatario?.cuit) && <div style={{ ...sx.mono, fontSize: text.sec, color: colors.textMeta }}>CUIT {p.cliente?.cuit || p.destinatario?.cuit}</div>}
        </div>
        <div><div style={lblSx}>Vendedor</div><div>{p.vendedor?.nombre || '—'}</div></div>
        <div><div style={lblSx}>Fecha</div><div style={sx.mono}>{fmtFecha(p.fecha)}</div></div>
        <div><div style={lblSx}>Válido hasta</div><div style={sx.mono}>{p.valido_hasta ? fmtFecha(p.valido_hasta) : '—'}</div></div>
        <div><div style={lblSx}>Forma de pago</div><div>{p.forma_pago || '—'}</div></div>
        <div><div style={lblSx}>IVA</div><div>{p.precios_con_iva ? 'Incluido en precios' : 'Se agrega aparte'}</div></div>
        {p.lista_precios && (
          <div><div style={lblSx}>Lista</div><div>{p.lista_precios} · precio {p.tipo_precio === 'lista' ? 'de lista' : 'de venta'}</div></div>
        )}
        {p.imprimir_precio_final && <div><div style={lblSx}>Reporte</div><div>Precio final, sin Dto</div></div>}
        <div><div style={lblSx}>Visto por el cliente</div>
          <div>{p.visto_cliente_at ? <>👁 <span style={sx.mono}>{fmtFechaHora(p.visto_cliente_at)}</span></> : '—'}</div></div>
      </div>

      {/* Versiones: toda la venta bajo el mismo número; acá se ve qué pasó con cada una */}
      {(p.versiones || []).length > 1 && (
        <div style={{ ...cardSx, padding: 0, overflow: 'hidden' }}>
          <table style={{ ...sx.table, fontSize: text.body }}>
            <thead>
              <tr style={{ background: colors.bgSoft }}>
                {['Versión', 'Qué pasó', 'Fecha', 'Estado', 'Total'].map((h, i) => (
                  <th key={h} style={{ ...sx.th, textAlign: i === 4 ? 'right' : 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...p.versiones].reverse().map(v => (
                <tr key={v.id} onClick={() => v.id !== p.id && navigate(`/presupuestos/${v.id}`)}
                  title={v.id === p.id ? 'Esta versión' : 'Ver esta versión'}
                  style={{ borderBottom: `1px solid ${colors.borderSoft}`, cursor: v.id === p.id ? 'default' : 'pointer', background: v.id === p.id ? colors.linkBg : 'transparent' }}
                  onMouseEnter={e => { if (v.id !== p.id) e.currentTarget.style.background = colors.bgSoft }}
                  onMouseLeave={e => { e.currentTarget.style.background = v.id === p.id ? colors.linkBg : 'transparent' }}>
                  <td style={{ padding: '8px 14px' }}><ChipVersion version={v.version} activa={v.id === p.id} /></td>
                  <td style={{ padding: '8px 14px', fontSize: text.sec, color: colors.textSec }}>{v.motivo_rechazo ? `Rechazada: "${v.motivo_rechazo}"` : (v.id === p.id ? 'Esta versión' : '')}</td>
                  <td style={{ padding: '8px 14px', ...sx.mono, fontSize: text.sec, color: colors.textSec }}>{fmtFechaHora(v.fecha)}</td>
                  <td style={{ padding: '8px 14px' }}><EstadoBadgePresupuesto estado={v.estado} /></td>
                  <td style={{ padding: '8px 14px', ...sx.mono, textAlign: 'right' }}>{v.total && v.total.desde !== v.total.hasta ? 'Múltiple valor' : fmtMonto(v.total?.desde ?? v.total_general)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Contenido */}
      <div style={cardSx}>
        <TablaLectura items={(p.items || []).filter(i => !i.grupo_id)} conIva={!p.precios_con_iva} cabecera />
        {(p.grupos || []).filter(g => g.tipo === 'suma').map(g => (
          <div key={g.id} style={{ marginTop: 10 }}>
            <div style={{ background: colors.bgSoft, color: colors.textSec, fontWeight: 600, fontSize: text.sec, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '6px 10px', borderRadius: 4 }}>{g.titulo}</div>
            <TablaLectura items={g.items} conIva={!p.precios_con_iva} cabecera={!(p.items || []).some(i => !i.grupo_id)} />
          </div>
        ))}
        {conjuntos.map(([nombre, ops]) => (
          <div key={nombre} style={{ marginTop: 14 }}>
            <div style={{ background: colors.primary, color: '#ffffff', fontWeight: 600, fontSize: text.sec, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '7px 10px', borderRadius: '4px 4px 0 0' }}>
              A elegir — {nombre}
            </div>
            {ops.map((op, i) => {
              const grupo = (p.grupos || []).find(g => g.id === op.grupo_id)
              return (
                <div key={op.grupo_id} style={{ border: `1px solid ${colors.borderSoft}`, borderTop: 'none', padding: '8px 10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, color: op.elegido ? colors.ok : colors.text }}>
                    <span>Opción {i + 1}{op.titulo && op.titulo.trim().toLowerCase() !== `opción ${i + 1}` ? `: ${op.titulo}` : ''} {op.elegido && '✔ ELEGIDA'}</span>
                    <span style={sx.mono}>{fmtMonto(op.escenario_total ?? op.total)}</span>
                  </div>
                  <TablaLectura items={grupo?.items || []} conIva={!p.precios_con_iva} compacta cabecera={i === 0 && !(p.items || []).some(x => !x.grupo_id) && !(p.grupos || []).some(g => g.tipo === 'suma')} />
                </div>
              )
            })}
          </div>
        ))}
      </div>

      {/* Totales — con opciones abiertas no hay total general: vale el total de cada opción */}
      <div style={{ ...cardSx, maxWidth: 440, marginLeft: 'auto' }}>
        {t.hay_opciones && !t.opciones_elegidas ? (
          <>
            {t.comun.total > 0 && <LineaTotal k="Ítems generales (entran en todas las opciones)" v={t.comun.total} />}
            {conjuntos.map(([nombre, ops]) => (
              <div key={nombre} style={{ marginTop: 8 }}>
                <div style={{ fontSize: text.meta, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, color: colors.textSec }}>{nombre}</div>
                {ops.map((op, i) => (
                  <div key={op.grupo_id} style={{ padding: '3px 0' }}>
                    {/* Lo que se suma al total se discrimina igual que el IVA (Martin, 6 oct
                        2026, PRE-20261006-QR6AF): mismo desglose que el PDF, del backend. */}
                    {(op.escenario_desglose || []).length > 1 && op.escenario_desglose.map((d, k) => (
                      <LineaTotal key={k} k={d.concepto} v={d.monto} />
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                      <span>Total Opción {i + 1}{op.titulo && op.titulo.trim().toLowerCase() !== `opción ${i + 1}` ? ` — ${op.titulo}` : ''}</span>
                      <span style={sx.mono}>{fmtMonto(op.escenario_total ?? op.total)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ))}
            <div style={{ fontSize: text.meta, color: colors.textMeta, marginTop: 6 }}>
              Cada total incluye los ítems generales{(t.extras || []).length > 0 ? ' y percepciones/retenciones' : ''}{p.precios_con_iva ? ' · IVA incluido' : ''}.
            </div>
          </>
        ) : (
          <>
            {!p.precios_con_iva && <>
              <LineaTotal k="Subtotal" v={t.comun.subtotal} />
              <LineaTotal k="IVA" v={t.comun.iva} />
            </>}
            {(t.extras || []).map((e, i) => (
              <LineaTotal key={i} k={`${e.tipo === 'percepcion' ? 'Percepción' : 'Retención'} ${e.concepto}${e.modo === 'porcentaje' ? ` (${e.valor}%)` : ''}`}
                v={e.tipo === 'retencion' ? -e.monto : e.monto} />
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `2px solid ${colors.border}`, paddingTop: 8, marginTop: 6, fontWeight: 700, fontSize: text.title }}>
              <span>TOTAL</span>
              <span style={sx.mono}>{fmtMonto(t.total_general)}</span>
            </div>
          </>
        )}
      </div>

      {/* Imágenes para el cliente (sug #95): de la versión, editables solo en borrador */}
      {(p.estado === 'borrador' || (p.imagenes || []).length > 0) && (
        <div style={cardSx}>
          <ImagenesPresupuesto presupuestoId={p.id} imagenes={p.imagenes || []} editable={p.estado === 'borrador'}
            onCambio={imgs => setP(prev => ({ ...prev, imagenes: imgs }))} />
        </div>
      )}

      {/* Historial */}
      {(p.historial || []).length > 0 && (
        <div style={cardSx}>
          <h3 style={{ marginTop: 0, fontSize: text.title }}>Historial</h3>
          {p.historial.map(h => (
            <div key={h.id} style={{ display: 'flex', gap: 10, fontSize: text.sec, color: colors.textSec, padding: '3px 0' }}>
              <span style={sx.mono}>{fmtFechaHora(h.created_at)}</span>
              <span>{h.estado_anterior ? `${LABELS_PRESUPUESTO[h.estado_anterior] || h.estado_anterior} → ` : ''}{LABELS_PRESUPUESTO[h.estado_nuevo] || h.estado_nuevo}</span>
              {h.usuario && <span>· {h.usuario.nombre}</span>}
              {h.motivo && <span>· {h.motivo}</span>}
            </div>
          ))}
        </div>
      )}

      {/* ── Modales ── */}
      {modal === 'aceptar' && (
        <Modal title="El cliente aceptó el presupuesto" onClose={() => setModal(null)} maxWidth="560px">
          {conjuntos.length > 0 ? (
            <>
              <p style={{ fontSize: text.sec, color: colors.textSec }}>Marcá qué opción eligió en cada conjunto:</p>
              {conjuntos.map(([nombre, ops]) => (
                <div key={nombre} style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>{nombre}</div>
                  {ops.map((op, i) => (
                    <label key={op.grupo_id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '5px 0', cursor: 'pointer' }}>
                      <input type="radio" name={nombre} checked={elegidas[nombre] === op.grupo_id}
                        onChange={() => setElegidas({ ...elegidas, [nombre]: op.grupo_id })} />
                      <span>Opción {i + 1}: {op.titulo}</span>
                      <span style={{ marginLeft: 'auto', ...sx.mono }}>{fmtMonto(op.total)}</span>
                    </label>
                  ))}
                </div>
              ))}
            </>
          ) : <p>Confirmás que el cliente aceptó el presupuesto {p.numero}?</p>}
          {error && <div style={{ color: colors.err, fontSize: text.sec }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <Boton variante="ghost" onClick={() => setModal(null)}>Cancelar</Boton>
            <Boton variante="success" disabled={trabajando || Object.keys(elegidas).length < conjuntos.length}
              onClick={() => accion(() => presupuestosApi.aceptar(p.id, Object.values(elegidas)))}>
              ✓ Confirmar aceptación
            </Boton>
          </div>
        </Modal>
      )}

      {modal === 'rechazar' && (
        <Modal title="El cliente rechazó el presupuesto" onClose={() => setModal(null)}>
          <label style={lblSx}>Motivo (opcional)</label>
          <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={3} style={{ ...inputSx, width: '100%', marginTop: 4 }}
            placeholder="Ej: precio alto, eligió otro proveedor, lo posterga…" />
          {/* El rechazo tiene dos salidas: corregir bajo el mismo número, o cerrar la venta */}
          <div style={{ ...lblSx, marginTop: 12 }}>¿Y ahora?</div>
          {[
            ['nueva_version', 'Hacer una versión nueva', 'Se corrige y se reenvía bajo el mismo número. Abre la v' + (p.version + 1) + ' para editar.'],
            ['perdido', 'Dar por perdido', 'Cierra la venta. Queda en rojo en el listado.'],
            ['', 'Decidir después', 'Queda rechazado; se puede versionar o dar por perdido más adelante.'],
          ].map(([v, l, d]) => (
            <label key={v} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '5px 0', cursor: 'pointer' }}>
              <input type="radio" name="cierre" checked={cierre === v} onChange={() => setCierre(v)} style={{ marginTop: 3 }} />
              <span><strong>{l}</strong><div style={{ fontSize: text.sec, color: colors.textSec }}>{d}</div></span>
            </label>
          ))}
          {error && <div style={{ color: colors.err, fontSize: text.sec }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <Boton variante="ghost" onClick={() => setModal(null)}>Cancelar</Boton>
            <Boton variante="destructive" disabled={trabajando} onClick={() => accion(async () => {
              const { data } = await presupuestosApi.rechazar(p.id, motivo, cierre || null)
              if (cierre === 'nueva_version' && data?.id && data.id !== p.id) navigate(`/presupuestos/${data.id}/editar`)
            }, cierre !== 'nueva_version')}>✕ Marcar rechazado</Boton>
          </div>
        </Modal>
      )}

      {modal === 'devolver' && (
        <Modal title="Devolver al vendedor" onClose={() => setModal(null)}>
          <p style={{ color: colors.textSec, fontSize: text.sec, marginTop: 0 }}>
            Se abre la v{p.version + 1} en borrador para corregirlo bajo el mismo número; la versión aceptada queda reemplazada. El motivo queda en el historial.
          </p>
          <label style={lblSx}>Motivo</label>
          <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={3} style={{ ...inputSx, width: '100%', marginTop: 4 }}
            placeholder="Ej: bonificados sin autorizar, condición de pago…" />
          {error && <div style={{ color: colors.err, fontSize: text.sec }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <Boton variante="ghost" onClick={() => setModal(null)}>Cancelar</Boton>
            <Boton variante="destructive" disabled={trabajando || !motivo.trim()} onClick={() => accion(async () => {
              const { data } = await presupuestosApi.devolver(p.id, motivo.trim())
              if (data?.id) navigate(`/presupuestos/${data.id}`)
            }, false)}>↩ Devolver</Boton>
          </div>
        </Modal>
      )}

      {modal === 'enviar' && (
        <Modal title={`Enviar ${p.numero} al cliente`} onClose={() => setModal(null)} maxWidth="560px">
          {/* Vía 1 — mail con PDF adjunto */}
          <div style={viaSx}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>✉ Por mail (PDF adjunto)</div>
            <label style={lblSx}>Para</label>
            <input value={mail.email} onChange={e => setMail({ ...mail, email: e.target.value })} placeholder="email@cliente.com"
              style={{ ...inputSx, width: '100%', marginTop: 4, marginBottom: 8 }} />
            <label style={lblSx}>Mensaje (opcional)</label>
            <textarea value={mail.mensaje} onChange={e => setMail({ ...mail, mensaje: e.target.value })} rows={2}
              style={{ ...inputSx, width: '100%', marginTop: 4 }} placeholder="Texto que acompaña al PDF adjunto" />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <Boton variante="send" disabled={trabajando || !mail.email}
                onClick={() => accion(() => presupuestosApi.enviarMail(p.id, mail.email, mail.mensaje || null))}>
                {trabajando ? 'Enviando…' : '✉ Enviar mail'}
              </Boton>
            </div>
          </div>

          {/* Vía 2 — WhatsApp con link al PDF */}
          <div style={viaSx}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>📱 Por WhatsApp</div>
                <div style={{ fontSize: text.sec, color: colors.textSec }}>Abre el chat con el link al PDF. Queda registrado en el historial.</div>
              </div>
              <Boton variante="send" onClick={compartirWA}>📱 Abrir WhatsApp</Boton>
            </div>
          </div>

          {/* Vía 3 — entregado fuera del sistema (solo tiene sentido en borrador) */}
          {p.estado === 'borrador' && (
            <div style={viaSx}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>🤝 Lo entregué por otra vía</div>
                  <div style={{ fontSize: text.sec, color: colors.textSec }}>Impreso o en mano — solo lo marca como enviado.</div>
                </div>
                <Boton variante="secondary" disabled={trabajando}
                  onClick={() => accion(() => presupuestosApi.enviar(p.id))}>→ Marcar enviado</Boton>
              </div>
            </div>
          )}

          {error && <div style={{ color: colors.err, fontSize: text.sec, marginTop: 4 }}>{error}</div>}
        </Modal>
      )}

      {modal === 'cambiarCliente' && (
        <Modal title="Cambiar cliente del presupuesto" onClose={() => setModal(null)} maxWidth="480px">
          <p style={{ color: colors.textSec, fontSize: text.sec, marginBottom: 12 }}>
            Reemplazá el cliente actual por uno del maestro. Los importes del presupuesto no cambian.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Boton variante="secondary" onClick={() => setModal('buscarCliente')}>🔍 Buscar en mis clientes</Boton>
            <Boton variante="secondary" onClick={() => setModal('altaCliente')}>+ Dar de alta un cliente nuevo</Boton>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <Boton variante="ghost" onClick={() => setModal(null)}>Cancelar</Boton>
          </div>
        </Modal>
      )}

      {modal === 'buscarCliente' && (
        <ModalListaClientes
          onSelect={asignarCliente}
          onClose={() => setModal('cambiarCliente')}
        />
      )}

      {modal === 'altaCliente' && (
        <ModalAltaCliente
          onClose={() => setModal('cambiarCliente')}
          onCreado={(cli) => asignarCliente(cli)}
        />
      )}
    </div>
  )
}

// Columnas FIJAS y compartidas por todas las tablas del detalle (renglones
// sueltos, grupos que suman y cada opción): Código · Detalle · Cantidad ·
// Precio · Dto · [IVA] · Monto. Martin, 12 sep 2026: "código, detalle,
// cantidad, precio, dto, monto, todo bien alineado" — antes el código iba
// pegado al detalle y cada tabla acomodaba sus anchos por su cuenta.
function ColumnasLectura({ conIva }) {
  return (
    <colgroup>
      <col style={{ width: 92 }} />
      <col />
      <col style={{ width: 64 }} />
      <col style={{ width: 118 }} />
      <col style={{ width: 84 }} />
      {conIva && <col style={{ width: 76 }} />}
      <col style={{ width: 128 }} />
    </colgroup>
  )
}

function TablaLectura({ items, conIva, compacta = false, cabecera = false }) {
  if (!items.length) return null
  const td = { padding: '6px 8px', verticalAlign: 'middle' }
  const num = { ...td, textAlign: 'right', ...sx.mono }
  const th = { ...sx.th, padding: '6px 8px' }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: compacta ? text.sec : text.body }}>
      <ColumnasLectura conIva={conIva} />
      {cabecera && (
        <thead>
          <tr>
            <th style={th}>Código</th>
            <th style={th}>Detalle</th>
            <th style={{ ...th, textAlign: 'right' }}>Cant.</th>
            <th style={{ ...th, textAlign: 'right' }}>Precio</th>
            <th style={{ ...th, textAlign: 'right' }}>Dto</th>
            {conIva && <th style={{ ...th, textAlign: 'right' }}>IVA</th>}
            <th style={{ ...th, textAlign: 'right' }}>Monto</th>
          </tr>
        </thead>
      )}
      <tbody>
        {items.map(i => (
          <tr key={i.id} style={{ borderBottom: `1px solid ${colors.borderSoft}` }}>
            <td style={{ ...td, ...sx.mono, fontSize: text.meta, color: colors.textMeta, whiteSpace: 'nowrap' }}>{i.articulo?.codigo_alfa || ''}</td>
            <td style={{ ...td, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={i.descripcion}>{i.descripcion}{!i.articulo_id && i.percibe_iva && <span style={{ marginLeft: 6, fontSize: text.meta, color: colors.textSec }}>· alimento o bebida (percibe IVA)</span>}</td>
            <td style={num}>{parseFloat(i.cantidad)}</td>
            <td style={num}>{fmtMonto(parseFloat(i.precio_unitario))}</td>
            <td style={{ ...num, color: colors.textSec }}>{parseFloat(i.descuento) > 0 ? `-${parseFloat(i.descuento)}%` : ''}</td>
            {conIva && <td style={{ ...num, color: colors.textMeta }}>{parseFloat(i.iva_porcentaje)}%</td>}
            <td style={{ ...num, fontWeight: 600 }}>{fmtMonto(parseFloat(i.cantidad) * parseFloat(i.precio_unitario) * (1 - parseFloat(i.descuento) / 100))}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function LineaTotal({ k, v }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', color: colors.textSec, fontSize: text.body }}>
      <span>{k}</span><span style={sx.mono}>{fmtMonto(v)}</span>
    </div>
  )
}
